import { isFeatureEnabled } from 'owa-feature-flags';
import folderStore, { getEffectiveFolderDisplayName } from 'owa-folders';
import { getStore as getMailSearchStore, setStaticSearchScope } from 'owa-mail-search';
import { setStaticSearchScope as setStaticSearchScopeNext } from 'owa-mail-search-next-lazy';
import getStaticSearchScope from 'owa-mail-search-next-lazy/lib/selectors/getStaticSearchScope';
import { lazyIsSearchBoxEmpty, SearchScenarioId, startSearch } from 'owa-search';
import { SearchScenarioId as SearchScenarioIdNext } from 'owa-search-next';
import * as React from 'react';
import {
    lazyIsSearchBoxEmpty as lazyIsSearchBoxEmptyNext,
    startSearch as startSearchNext,
} from 'owa-search-next-lazy';

const classNames = require('classnames');
const styles = require('./FolderTag.scss');

export interface FolderTagProps {
    folderId: string; // The ID of the folder being represented
    isPreviewTextOn: boolean; // If preview text is displayed in the list view
    isSingleLine: boolean; // If tag is being displayed in SLV
    isUnread: boolean; // If tag is part of a mail item that is unread
}

export default class FolderTag extends React.Component<FolderTagProps, {}> {
    render() {
        const { folderId, isPreviewTextOn, isSingleLine, isUnread } = this.props;
        const folder = folderStore.folderTable.get(folderId);

        // We found out that search can sometimes return an item from a folder that does
        // not exist in the folder table. If that is the case we do not want to render
        // the folder tag.
        // VSO: 26898 - Search returns an item from a folder that does not exist in store
        if (!folder) {
            return null;
        }

        /**
         * Determine the color of the tag based on if underlying mail item is
         * unread or not.
         */
        const colorClassName = isUnread ? styles.unreadItemFolderTag : styles.readItemFolderTag;

        /**
         * Determine styles on the outer container based on if user is in SLV
         * or not.
         */
        const containerClassName = isSingleLine
            ? styles.folderTagContainer
            : styles.folderTagContainerThreeColumn;

        /**
         * Give container a right margin if the user is in 3 column view and has
         * preview text off. In this scenario, the folder tag will flow with
         * categories (if present) in a single line.
         */
        const rightMarginClassName =
            !isSingleLine && !isPreviewTextOn ? styles.folderTagContainerRightMargin : '';

        /**
         * Give container a left margin if the user is in 3 column view and has preview
         * text on or if user is in SLV.
         */
        const leftMarginClassName =
            (!isSingleLine && isPreviewTextOn) || isSingleLine
                ? styles.folderTagContainerLeftMargin
                : '';

        // Concatenate styles for container.
        const folderTagContainerClassNames = classNames([
            colorClassName,
            containerClassName,
            rightMarginClassName,
            leftMarginClassName,
            !this.shouldFolderTagBeClickable() && styles.unclickableFolderTag,
        ]);

        const folderName = getEffectiveFolderDisplayName(folder);

        return (
            <button className={folderTagContainerClassNames} onClick={this.onFolderTagClick}>
                <span className={styles.folderTagLabel} title={folderName}>
                    {folderName}
                </span>
            </button>
        );
    }

    private checkSearchBoxEmpty(): boolean {
        if (isFeatureEnabled('sea-lazy2')) {
            const isSearchBoxEmptyNext = lazyIsSearchBoxEmptyNext.tryImportForRender();
            return (
                // undefined means search is not initialized which means the search box is empty
                isSearchBoxEmptyNext === undefined ||
                (isSearchBoxEmptyNext && isSearchBoxEmptyNext(SearchScenarioIdNext.Mail))
            );
        } else {
            const isSearchBoxEmpty = lazyIsSearchBoxEmpty.tryImportForRender();
            return (
                // undefined means search is not initialized which means the search box is empty
                isSearchBoxEmpty === undefined ||
                (isSearchBoxEmpty && isSearchBoxEmpty(SearchScenarioId.Mail))
            );
        }
    }

    private shouldFolderTagBeClickable(): boolean {
        if (this.checkSearchBoxEmpty()) {
            return false;
        }

        let staticSearchScope;
        if (isFeatureEnabled('sea-lazy2')) {
            staticSearchScope = getStaticSearchScope();
        } else {
            staticSearchScope = getMailSearchStore().staticSearchScope;
        }

        return staticSearchScope.folderId !== this.props.folderId;
    }

    private onFolderTagClick = async (ev: React.MouseEvent<unknown>) => {
        // Stop click so it doesn't get acted on by MailListItem.
        ev.stopPropagation();

        /**
         * If user cleared search box between search and clicking folder
         * tag, it's a no-op.
         */

        if (!this.shouldFolderTagBeClickable()) {
            return;
        }

        let staticSearchScope;
        if (isFeatureEnabled('sea-lazy2')) {
            staticSearchScope = getStaticSearchScope();
        } else {
            staticSearchScope = getMailSearchStore().staticSearchScope;
        }

        // Create new search scope to match folder represented by folder tag.
        let searchScope = null;
        searchScope = {
            kind: staticSearchScope.kind,
            folderId: this.props.folderId,
        };

        if (isFeatureEnabled('sea-lazy2')) {
            setStaticSearchScopeNext(searchScope);
            startSearchNext('FolderTag', SearchScenarioIdNext.Mail, false /* explicitSearch */);
        } else {
            setStaticSearchScope(searchScope);
            startSearch('FolderTag', SearchScenarioId.Mail, false /* explicitSearch */);
        }
    };
}
